/**
 * Hook for managing contact resolution API operations
 */

import { useState, useCallback } from 'react'
import { contactResolutionApiClient } from '@/lib/api/contact-resolution.client'
import type {
  ContactResolutionRequest,
  ContactResolutionResponse,
  ValidateContactResolutionResponse,
} from '@/lib/api/contact-resolution.client'
import type { ContactResolutionPlan } from '@super/shared-types'
import { IntegrationSource } from '@super/shared-types'

interface UseContactResolutionResult {
  isLoading: boolean
  isValidating: boolean
  isExecuting: boolean
  executeContactResolution: (
    resolutionPlan: ContactResolutionPlan,
    companyId: string,
    source: IntegrationSource,
    options?: ContactResolutionRequest['options'],
  ) => Promise<ContactResolutionResponse>
  validateContactResolution: (
    resolutionPlan: ContactResolutionPlan,
    companyId: string,
    source: IntegrationSource,
    options?: ContactResolutionRequest['options'],
  ) => Promise<ValidateContactResolutionResponse>
}

export function useContactResolution(): UseContactResolutionResult {
  const [isValidating, setIsValidating] = useState(false)
  const [isExecuting, setIsExecuting] = useState(false)

  const executeContactResolution = useCallback(
    async (
      resolutionPlan: ContactResolutionPlan,
      companyId: string,
      source: IntegrationSource,
      options?: ContactResolutionRequest['options'],
    ): Promise<ContactResolutionResponse> => {
      setIsExecuting(true)
      try {
        const response =
          await contactResolutionApiClient.executeContactResolution({
            contactResolutionPlan: resolutionPlan,
            companyId,
            source,
            options,
          })
        return response
      } finally {
        setIsExecuting(false)
      }
    },
    [],
  )

  const validateContactResolution = useCallback(
    async (
      resolutionPlan: ContactResolutionPlan,
      companyId: string,
      source: IntegrationSource,
      options?: ContactResolutionRequest['options'],
    ): Promise<ValidateContactResolutionResponse> => {
      setIsValidating(true)
      try {
        const response =
          await contactResolutionApiClient.validateContactResolution({
            contactResolutionPlan: resolutionPlan,
            companyId,
            source,
            options,
          })
        return response
      } finally {
        setIsValidating(false)
      }
    },
    [],
  )

  return {
    isLoading: isValidating || isExecuting,
    isValidating,
    isExecuting,
    executeContactResolution,
    validateContactResolution,
  }
}
